<template>
	<v-app>
		<v-main>
			<v-container class="widget-layout" :class="{ 'widget-layout--myday-fullscreen': showWidgetDateContainer }">
				<div v-show="showWidgetDateContainer" class="widget-date-container">
					<md-icon @click="goToPreviousDate">chevron_left</md-icon>

					<v-menu v-model="menu" v-bind="menuProps" location="bottom center" teleport="false" :close-on-content-click="false" transition="scale-transition" offset-y>
						<template #activator="{ props }">
							<v-text-field class="text-center" v-model="formattedDate" readonly v-bind="props" variant="plain" />
						</template>
						<v-date-picker v-model="selectedDate" @update:model-value="updateFormattedDate" density="compact" color="primary" show-adjacent-months hide-header>
							<template #actions>
								<v-btn variant="text" color="primary" @click="setToday">{{ t('Today') }}</v-btn>
							</template>
						</v-date-picker>
					</v-menu>

					<md-icon @click="goToNextDate">chevron_right</md-icon>

					<md-icon v-if="fullscreenWidget === null && !isTablet" class="old-widget-config-icon" @click="oldWidgetConfigClick">settings</md-icon>
				</div>

				<div class="widget-layout-grid">
					<template v-if="sortedWidgets">
						<component v-for="widget in sortedWidgets" :key="widget.id" :is="widget.component" :ref="(el) => registerWidgetRef(el, widget.id.toLowerCase())" :title="widget.title" :selectedDate="selectedDate" :params="widget.params" :guid="widget.guid" :style="getWidgetGridStyle(widget)" v-show="widget.visible" />
					</template>
					<template v-else>
						<div v-for="skeleton in skeletonWidgets" :key="skeleton.id" class="widget widget-skeleton" :class="{ 'widget-skeleton--circle': skeleton.circle }" :style="getWidgetGridStyle(skeleton)">
							<v-progress-circular v-if="skeleton.circle" indeterminate color="grey-lighten-1" :width="12" />
							<v-card v-else class="mx-auto" flat>
								<v-skeleton-loader :type="skeleton.type" />
							</v-card>
						</div>
						<v-snackbar v-model="widgetsLoading" :timeout="-1" :loading="true" rounded="xl" color="info">
							{{ t('widgetsLoading') }}
						</v-snackbar>
					</template>
				</div>

				<v-dialog v-model="widgetSettingsDialog" :attach="overlayTarget">
					<v-card>
						<v-card-title class="headline">
							<md-icon>widgets</md-icon>
							{{ t('WidgetsList') }}
						</v-card-title>
						<v-card-text>
							<AgGrid ref="widgetSettingsGrid" v-model:rowData="sortedWidgets" :colDefs="widgetSettingsGridColumns" row-group-panel-show="false"></AgGrid>
						</v-card-text>
						<v-card-actions>
							<v-btn color="error" variant="text" @click="resetWidgetSettings">{{ t('Reset') }}</v-btn>
							<v-btn text @click="widgetSettingsDialog = false" data-close>{{ t('Close') }}</v-btn>
							<v-btn variant="tonal" @click="saveWidgetSettings">{{ t('Save') }}</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</v-container>
		</v-main>
	</v-app>
</template>

<script setup>
import { ref, watch, inject, computed, onMounted, onBeforeUnmount, provide, markRaw, nextTick } from 'vue';
import { addDays, subDays, format, min } from 'date-fns';
import { fr, enGB, es, pt, nl, de } from 'date-fns/locale';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/utils/useTranslator';
import { eventBus } from '@/utils/eventBus';
import { deviceUtils } from '@/utils/deviceUtils';
import { isEmbeddedInBackOffice } from '@/utils/boContext';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import { getWidgets, WidgetEnum } from '@/api/core/widget';
import { isSyncCompleted, isBackgroundSyncCompleted } from '@/sync/dataSync';
import { kpiService } from '@/services/kpiService';

import WidgetMyDay from './WidgetMyDay.vue';
import WidgetMyArea from './WidgetMyArea.vue';
import WidgetKpi from './WidgetKpi.vue';
import AgGrid from '../agGrid/AgGrid.vue';
import VisibilitySwitchRenderer from '@/components/agGrid/VisibilitySwitchRenderer.vue';
import ButtonIconRenderer from '@/components/agGrid/ButtonIconRenderer.vue';
import { overlayHandler } from '@/utils/overlayHandler';
import { log } from '@/utils/vueLogger';
import { getUserPoints } from '@/api/sales/points';
import { isScoringMatrixAvailable } from '@/services/pointsMatrixService';

// #region DATA

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

const widgetComponents = {
	WidgetMyDay: markRaw(WidgetMyDay),
	WidgetMyArea: markRaw(WidgetMyArea),
	WidgetKpi: markRaw(WidgetKpi),
};

const iframeDocument = iframeDataBridge.getIframeWindow().document;
const user = iframeDataBridge.getCurrentUser();
const userSettings = ref([]);
const widgetsLoading = ref(true);
const isTablet = ref(deviceUtils.isTablet());
const skeletonWidgets = [
	{ id: 'skeleton-myday', columns: 2, rows: 2, type: 'card, list-item, list-item-two-line, list-item-two-line' },
	{ id: 'skeleton-myarea', columns: 2, rows: 2, type: 'list-item, list-item-two-line' },
	{ id: 'skeleton-kpi-1', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-2', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-3', columns: 2, rows: 2, circle: true },
	{ id: 'skeleton-kpi-4', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-5', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-6', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-7', columns: 1, rows: 1, circle: true },
];
const selectedDate = ref(new Date());
const formattedDate = ref('');
const menu = ref(false);
watch(menu, () => {
	overlayHandler(menu);
});
const menuProps = computed(() => ({
	attach: overlayTarget || undefined,
	scrim: true,
	scrollStrategy: 'close',
}));
const widgetSettingsDialog = ref(false);
const widgetSettingsGrid = ref(null);
const widgetSettingsGridColumns = ref([]);
const activeGridColumns = ref(getGridColumnsForViewport(window.innerWidth));
const fullscreenWidget = ref(null);
const showWidgetDateContainer = computed(() => fullscreenWidget.value === 'widget-myday' || fullscreenWidget.value === null);

const widgetRefs = new Map();
function registerWidgetRef(el, id) {
	if (el) widgetRefs.set(id.toLowerCase(), el);
	else widgetRefs.delete(id.toLowerCase());
}

const mapControl = {
	zoomToLocation: (locationId) => {},
	drawRoutes: (locations) => {},
};

provide('mapControl', mapControl);

// #endregion

// #region LIFECYCLE

const init = async () => {
	activeGridColumns.value = getGridColumnsForViewport(window.innerWidth);
	injectWidgetSettingsMenu();
	injectPointsBadge();
	await getWidgetSettings();
	setWidgetSettingsGridData();

	eventBus.on('widget-fullscreened', resizeLayout);
	eventBus.on('kpi-simulated-changed', onKpiSimulatedChanged);
	eventBus.on('custom-kpi-saved', onKpiCustomSaved);
	eventBus.on('vue-persistent-zone-replaced', onPersistentZoneReplaced);
	window.addEventListener('resize', handleWindowResize);
	window.addEventListener('points-claimed', injectPointsBadge);
	window.addEventListener('points-updated', injectPointsBadge);
};

const onOpenWidgetDialog = () => {
	widgetSettingsDialog.value = true;
};

const isFullySynced = () => isSyncCompleted() && isBackgroundSyncCompleted();

const initWhenFullySynced = () => {
	if (!isFullySynced()) return;
	eventBus.off('main-sync-completed', initWhenFullySynced);
	eventBus.off('background-sync-completed', initWhenFullySynced);
	init();
};

onMounted(() => {
	if (isEmbeddedInBackOffice()) {
		log('info', 'WidgetLayout', 'Embedded in Back-Office — skipping widget load');
		return;
	}

	window.addEventListener('open-vue-widget-dialog', onOpenWidgetDialog);

	if (isFullySynced()) {
		init();
	} else {
		eventBus.on('main-sync-completed', initWhenFullySynced);
		eventBus.on('background-sync-completed', initWhenFullySynced);
	}
});

onBeforeUnmount(() => {
	eventBus.off('main-sync-completed', initWhenFullySynced);
	eventBus.off('background-sync-completed', initWhenFullySynced);
	eventBus.off('widget-fullscreened', resizeLayout);
	eventBus.off('kpi-simulated-changed', onKpiSimulatedChanged);
	eventBus.off('custom-kpi-saved', onKpiCustomSaved);
	eventBus.off('vue-persistent-zone-replaced', onPersistentZoneReplaced);
	window.removeEventListener('resize', handleWindowResize);
	window.removeEventListener('points-claimed', injectPointsBadge);
	window.removeEventListener('points-updated', injectPointsBadge);
	window.removeEventListener('open-vue-widget-dialog', onOpenWidgetDialog);
});

// #endregion

// #region SELECTED DATE

const DATE_FNS_LOCALES = { fr, en: enGB, es, pt, nl, de };

watch(selectedDate, updateFormattedDate, { immediate: true });

function updateFormattedDate() {
	const rawLocale = iframeDataBridge.getCurrentUser()?.locale;
	const key = rawLocale ? rawLocale.split('-')[0] : (navigator.language?.split('-')[0] ?? 'fr');
	const locale = DATE_FNS_LOCALES[key] ?? fr;
	formattedDate.value = format(new Date(selectedDate.value), 'd MMMM yyyy', { locale });
	menu.value = false;
}

function goToPreviousDate() {
	selectedDate.value = subDays(new Date(selectedDate.value), 1);
	updateFormattedDate(selectedDate.value);
}

function goToNextDate() {
	selectedDate.value = addDays(new Date(selectedDate.value), 1);
	updateFormattedDate(selectedDate.value);
}

function setToday() {
	selectedDate.value = new Date();
	updateFormattedDate(selectedDate.value);
}

// #endregion

// #region SETTINGS

const baseWidgetsTemplate = [
	{ id: 'widget-myday', key: WidgetEnum.MyDay, component: 'WidgetMyDay', title: t('Home_My_Planning'), visible: true, mandatory: true, columns: 2, rows: 2, index: 1 },
	{ id: 'widget-myArea', key: WidgetEnum.MyArea, component: 'WidgetMyArea', title: t('Home_Scope'), visible: true, mandatory: false, columns: 2, rows: 2, index: 2 },
];

function buildKpiWidgets(startIndex = 3) {
	try {
		const kpiList = kpiService.getAvailableKpis(true);
		return kpiList.map((kpi, idx) => {
			const reference = (kpi.reference || '').toLowerCase();
			const widgetId = `widget-${reference}`;

			return {
				id: widgetId,
				key: reference,
				component: 'WidgetKpi',
				title: kpi.referenceTranslated || kpi.reference,
				visible: true,
				mandatory: false,
				columns: 1,
				rows: 1,
				index: startIndex + idx,
				params: {
					type: 'kpi',
					shape: 'circle',
					reference: kpi.reference,
					icon: kpi.icon || 'star',
					simulated: false,
				},
			};
		});
	} catch (error) {
		log('error', 'WidgetLayout', `Error building KPI widgets: ${error.message}`);
		return [];
	}
}

const baseWidgetIds = new Set(baseWidgetsTemplate.map((w) => w.id));
let widgetsConfig = [];

const sortedWidgets = ref(null);

provide('sortedWidgets', sortedWidgets);

function getWidgetMinSpan(widgetId) {
	return ['widget-myday', 'widget-myArea'].includes(widgetId) ? 2 : 1;
}

function clampGridSpan(value, widgetId = null) {
	const parsed = Number.parseInt(value, 10);
	const min = widgetId ? getWidgetMinSpan(widgetId) : 1;
	if (Number.isNaN(parsed)) {
		return min;
	}

	return Math.min(Math.max(parsed, min), 6);
}

function getGridColumnsForViewport(width) {
	if (width >= 1920) return 9;
	if (width >= 1600) return 8;
	if (width >= 1280) return 7;
	if (width >= 1100) return 6;
	if (width >= 960) return 5;
	if (width >= 800) return 4;
	if (width >= 600) return 3;
	return 2;
}

function getWidgetGridStyle(widget) {
	const widgetColumns = clampGridSpan(widget?.columns);
	const widgetRows = clampGridSpan(widget?.rows);
	const effectiveColumns = Math.min(widgetColumns, activeGridColumns.value);

	return {
		gridColumn: `span ${effectiveColumns}`,
		gridRow: `span ${widgetRows}`,
	};
}

async function getWidgetSettings() {
	userSettings.value = await getUserSettings({ groupName: 'WEBAPP', userId: user.id });
	widgetsConfig = [...baseWidgetsTemplate, ...buildKpiWidgets()];
	let widgetSettings = userSettings.value.find((s) => s.name === 'VueWidgets');

	if (!widgetSettings) {
		widgetSettings = {
			name: 'VueWidgets',
			groupName: 'WEBAPP',
			value: JSON.stringify(widgetsConfig),
		};
		user.settings.push(widgetSettings);
	} else {
		const settings = JSON.parse(widgetSettings.value);
		widgetsConfig.forEach((widget) => {
			const existingWidget = settings.find((w) => w.id === widget.id);

			if (existingWidget) {
				// Keep the freshly-built, translated title (from buildKpiWidgets / baseWidgetsTemplate)
				// instead of the literal title frozen in the saved VueWidgets setting.
				const translatedTitle = widget.title;
				Object.assign(widget, existingWidget);
				widget.title = translatedTitle;
				widget.columns = clampGridSpan(widget.columns, widget.id);
				widget.rows = clampGridSpan(widget.rows, widget.id);

				if (existingWidget.simulated !== undefined && widget.params && 'simulated' in widget.params) {
					widget.params = { ...widget.params, simulated: existingWidget.simulated };
				}
			}
		});

		// Restore custom KPIs not in the base config, dropping settings whose custom KPI no longer exists
		// (e.g. deleted from the client platform) instead of restoring a widget that can never load data.
		const widgetsConfigIds = new Set(widgetsConfig.map((w) => w.id));
		const orphanedCustomWidgetIds = new Set();
		settings
			.filter((s) => !baseWidgetIds.has(s.id) && s.componentKey && !widgetsConfigIds.has(s.id))
			.forEach((customWidget) => {
				if (customWidget.params?.reference && !kpiService.kpiExists(customWidget.params.reference)) {
					orphanedCustomWidgetIds.add(customWidget.id);
					return;
				}

				const customParams = normalizeCustomKpiParams(customWidget.params, widgetsConfig);
				widgetsConfig.push({
					id: customWidget.id,
					key: customWidget.id,
					component: customWidget.componentKey,
					title: customWidget.title,
					visible: customWidget.visible ?? true,
					mandatory: false,
					columns: clampGridSpan(customWidget.columns, customWidget.id),
					rows: clampGridSpan(customWidget.rows, customWidget.id),
					index: customWidget.index,
					custom: true,
					guid: customWidget.guid || customWidget.id,
					params: customParams,
				});
			});

		if (orphanedCustomWidgetIds.size > 0) {
			widgetSettings.value = JSON.stringify(settings.filter((s) => !orphanedCustomWidgetIds.has(s.id)));
			userSettings.value = userSettings.value.map((s) => (s.name === 'VueWidgets' ? widgetSettings : s));
			await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });
			log('info', 'WidgetLayout', `Removed ${orphanedCustomWidgetIds.size} orphaned custom KPI widget setting(s) for deleted KPI(s)`);
		}
	}

	await filterOrganisationWidgets(widgetsConfig);
	sortedWidgets.value = widgetsConfig.sort((a, b) => a.index - b.index).map((w) => ({ ...w, componentKey: w.component, component: widgetComponents[w.component] }));
}

async function filterOrganisationWidgets(widgetsConfig) {
	try {
		const organisationWidgetParams = await getWidgets({ id: user.organisationId });

		if (organisationWidgetParams) {
			organisationWidgetParams.widgets.forEach((organisationWidget) => {
				// Remove widgets that are hidden by the organisation
				if (organisationWidget.state == 2) {
					widgetsConfig = widgetsConfig.filter((w) => w.key !== organisationWidget.key);
				}

				// Handle mandatory widgets
				const widget = widgetsConfig.find((w) => w.key === organisationWidget.key);
				if (widget && organisationWidget.state == 1) {
					widget.visible = true;
				}

				return widgetsConfig;
			});
		}
	} catch (error) {
		log('error', 'WidgetLayout', `❌ Error filtering widgets by organisation: ${error.message}`);
	}
}

function injectWidgetSettingsMenu() {
	let document = iframeDataBridge.getIframeWindow().document;
	const items = document.querySelectorAll('.nav-user-actions-sub-menu ul li');
	const hasItem = items.length > 0 && Array.from(items).some((item) => item.classList.contains('widget-settings-menu-item'));

	if (items.length >= 3 && !hasItem) {
		const li = document.createElement('li');
		li.classList.add('nav-user-actions-sub-menu-item');
		li.classList.add('widget-settings-menu-item');
		li.innerHTML = `
                <a href="#" id="widget-settings-menu">
                    <md-icon>widgets</md-icon>
                    <span>${t('WidgetsSettings')}</span>
                </a>
            `;
		items[0].after(li);

		document.getElementById('widget-settings-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-widget-dialog'));
		});
	}

	const mobileMenu = document.querySelector('nav .nav-open-container');
	const mobileWidgetItem = mobileMenu?.querySelector('.mobile-widget-settings-menu-item');
	const hasMobileItem = mobileMenu && mobileWidgetItem;

	if (!hasMobileItem) {
		const ul = document.createElement('ul');
		const li = document.createElement('li');
		li.classList.add('mobile-widget-settings-menu-item');
		li.innerHTML = `
				<a href="#" id="mobile-widget-settings-menu">
					<md-icon>widgets</md-icon>
					<span>${t('WidgetsSettings')}</span>
				</a>
			`;
		ul.appendChild(li);

		const updateMenu = mobileMenu?.querySelector('.update-menu');
		if (updateMenu) {
			updateMenu.after(ul);
		} else if (mobileMenu) {
			mobileMenu.appendChild(ul);
		}

		document.getElementById('mobile-widget-settings-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-widget-dialog'));
			document.querySelector('.nav-open-container').style.display = 'none';
		});
	}
}

function removePointsBadges(iframeDoc) {
	iframeDoc.querySelector('.points-user-badge-menu-item')?.remove();
	iframeDoc.querySelector('.mobile-points-user-badge-menu-item')?.remove();
}

async function injectPointsBadge() {
	const iframeDoc = iframeDataBridge.getIframeWindow().document;

	if (!isScoringMatrixAvailable()) {
		removePointsBadges(iframeDoc);
		return;
	}

	const pts = await getUserPoints();

	if (!pts?.season) {
		removePointsBadges(iframeDoc);
		return;
	}

	const totalPoints = pts?.total ?? 0;

	const desktopItems = iframeDoc.querySelectorAll('.nav-user-actions-sub-menu ul li');
	const hasDesktopBadge = desktopItems.length > 0 && Array.from(desktopItems).some((item) => item.classList.contains('points-user-badge-menu-item'));

	if (desktopItems.length >= 3 && !hasDesktopBadge) {
		const li = iframeDoc.createElement('li');
		li.classList.add('nav-user-actions-sub-menu-item', 'points-user-badge-menu-item');
		li.innerHTML = `
			<a href="#" id="points-user-badge-menu" style="display:flex;align-items:center;gap:6px;">
				<md-icon>paid</md-icon>
				<span id="points-user-badge-count">${t('YourPoints')} ${totalPoints} ${t('pts')}</span>
			</a>
		`;
		desktopItems[0].after(li);

		iframeDoc.getElementById('points-user-badge-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-points-dialog'));
		});
	} else {
		const badge = iframeDoc.getElementById('points-user-badge-count');
		if (badge) badge.textContent = `${t('YourPoints')} ${totalPoints} ${t('pts')}`;
	}

	const mobileMenu = iframeDoc.querySelector('nav .nav-open-container');
	const hasMobileBadge = mobileMenu?.querySelector('.mobile-points-user-badge-menu-item');

	if (mobileMenu && !hasMobileBadge) {
		const ul = iframeDoc.createElement('ul');
		const li = iframeDoc.createElement('li');
		li.classList.add('mobile-points-user-badge-menu-item');
		li.innerHTML = `
			<a href="#" id="mobile-points-user-badge-menu" style="display:flex;align-items:center;gap:6px;">
				<md-icon>paid</md-icon>
				<span id="mobile-points-user-badge-count">${t('YourPoints')} ${totalPoints} ${t('pts')}</span>
			</a>
		`;
		ul.appendChild(li);

		const widgetMenu = mobileMenu.querySelector('.mobile-widget-settings-menu-item')?.parentElement;
		if (widgetMenu) {
			widgetMenu.after(ul);
		} else {
			mobileMenu.appendChild(ul);
		}

		iframeDoc.getElementById('mobile-points-user-badge-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-points-dialog'));
			iframeDoc.querySelector('.nav-open-container').style.display = 'none';
		});
	}
}

function setWidgetSettingsGridData() {
	widgetSettingsGridColumns.value = [
		{ field: 'index', headerName: t('Position'), width: 100, rowDrag: true, sortable: false, filter: false },
		{ field: 'title', headerName: t('Title'), width: 300, sortable: false, filter: false },
		{ field: 'columns', headerName: `${t('Width')}`, width: 120, sortable: false, filter: false, editable: true, cellEditor: 'agNumberCellEditor', cellEditorParams: (params) => ({ min: getWidgetMinSpan(params.data.id), max: 6, step: 1, showStepperButtons: true }), valueParser: parseGridSpanValue },
		{ field: 'rows', headerName: `${t('Height')}`, width: 120, sortable: false, filter: false, editable: true, cellEditor: 'agNumberCellEditor', cellEditorParams: (params) => ({ min: getWidgetMinSpan(params.data.id), max: 6, step: 1, showStepperButtons: true }), valueParser: parseGridSpanValue },
		{ field: 'visible', headerName: t('Visible'), width: 180, sortable: false, filter: false, cellRenderer: VisibilitySwitchRenderer },
		{
			field: 'delete',
			headerName: '',
			width: 80,
			minWidth: 80,
			maxWidth: 80,
			sortable: false,
			filter: false,
			cellRendererSelector: (params) => {
				if (!params.data.custom && !params.data.guid) return null;
				return { component: ButtonIconRenderer };
			},
			cellRendererParams: (params) => ({
				icon: 'delete',
				tooltip: t('Delete'),
				key: params.data.id,
				onClick: (key) => {
					const index = sortedWidgets.value.findIndex((w) => w.id === key);
					if (index !== -1) {
						sortedWidgets.value.splice(index, 1);
					}
					saveWidgetSettings();
				},
			}),
			suppressHeaderMenuButton: true,
		},
	];
}

function parseGridSpanValue(params) {
	const parsed = Number.parseInt(params.newValue, 10);
	const widgetParams = params.data.params;
	const min = getWidgetMinSpan(params.data.id);

	if (Number.isNaN(parsed)) {
		return params.oldValue;
	}

	const result = Math.min(Math.max(parsed, min), 6);

	// update same size if circle
	if (widgetParams && widgetParams.shape === 'circle') {
		// update row column and row
		if (params.colDef.field === 'columns') {
			params.data.rows = result;
		} else if (params.colDef.field === 'rows') {
			params.data.columns = result;
		}
	}

	return result;
}

async function saveWidgetSettings(closeDialog = true) {
	widgetSettingsGrid.value?.getGridApi()?.stopEditing();
	const updatedWidgetsConfig = sortedWidgets.value.map((widget) => {
		const isCustom = !baseWidgetIds.has(widget.id);
		return {
			id: widget.id,
			visible: widget.visible,
			columns: clampGridSpan(widget.columns, widget.id),
			rows: clampGridSpan(widget.rows, widget.id),
			index: widget.index,
			guid: widget.guid,
			...(widget.params && 'simulated' in widget.params ? { simulated: widget.params.simulated } : {}),
			...(isCustom ? { title: widget.title, componentKey: widget.componentKey, params: widget.params } : {}),
		};
	});

	userSettings.value = userSettings.value.filter((s) => s.name !== 'VueWidgets');
	userSettings.value.push({
		name: 'VueWidgets',
		groupName: 'WEBAPP',
		value: JSON.stringify(updatedWidgetsConfig),
	});
	await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });

	if (closeDialog) widgetSettingsDialog.value = false;
}

async function resetWidgetSettings() {
	try {
		userSettings.value = userSettings.value.filter((s) => s.name !== 'VueWidgets');
		await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });

		widgetsConfig = [];
		await getWidgetSettings();
		setWidgetSettingsGridData();

		widgetSettingsDialog.value = false;
		log('info', 'WidgetLayout', 'Widget settings reset to defaults');
	} catch (error) {
		log('error', 'WidgetLayout', `Error resetting widget settings: ${error.message}`);
	}
}

function onKpiSimulatedChanged({ guid, reference, isSimulated }) {
	const normalizedReference = String(reference || '').toLowerCase();
	const widget = !guid ? sortedWidgets.value.find((w) => String(w?.params?.reference || '').toLowerCase() === normalizedReference) : sortedWidgets.value.find((w) => w.id === guid);
	if (widget?.params) {
		widget.params.simulated = isSimulated;
		saveWidgetSettings(false);
	}
}

function normalizeCustomKpiParams(params = {}, widgetsCollection = sortedWidgets.value) {
	const normalizedParams = { type: 'kpi', shape: 'circle', ...params };
	const sourceReference = params?.reference;
	const sourceWidget = sourceReference && Array.isArray(widgetsCollection) ? widgetsCollection.find((widget) => !widget?.guid && widget?.params?.reference?.toLowerCase() === sourceReference.toLowerCase()) : null;
	const sourceKpi = sourceReference ? (iframeDataBridge.getEcosVM()?.kpis || []).find((kpi) => kpi?.reference?.toLowerCase() === sourceReference.toLowerCase()) : null;

	normalizedParams.icon = normalizedParams.icon || sourceWidget?.params?.icon || sourceKpi?.icon || 'star';
	const sourceSupportsSimulation = !!(sourceWidget?.params && 'simulated' in sourceWidget.params);

	if (sourceSupportsSimulation) {
		normalizedParams.simulated = normalizedParams.simulated ?? false;
	} else {
		delete normalizedParams.simulated;
	}

	return normalizedParams;
}

function onKpiCustomSaved({ guid, params, annotation, addedToHome }) {
	const existingWidgetIndex = sortedWidgets.value.findIndex((w) => w.id === guid);
	const customParams = normalizeCustomKpiParams(params);

	if (existingWidgetIndex !== -1) {
		// Update existing custom KPI
		if (addedToHome) {
			sortedWidgets.value[existingWidgetIndex] = { id: guid, component: widgetComponents['WidgetKpi'], componentKey: 'WidgetKpi', title: annotation, visible: true, mandatory: false, columns: 1, rows: 1, index: sortedWidgets.value.length + 1, guid: guid, params: customParams };
		} else {
			sortedWidgets.value.splice(existingWidgetIndex, 1);
		}
	} else if (addedToHome) {
		// Add new custom KPI
		sortedWidgets.value.push({ id: guid, component: widgetComponents['WidgetKpi'], componentKey: 'WidgetKpi', title: annotation, visible: true, mandatory: false, columns: 1, rows: 1, index: sortedWidgets.value.length + 1, guid: guid, params: customParams });
	}

	saveWidgetSettings(false);
}

function oldWidgetConfigClick() {
	const externis = iframeDataBridge.getIframeWindow().externis;
	externis.sales.vwWidgetsVM.widgetSettingsHandler();
}

// #endregion

// #region UI

async function onPersistentZoneReplaced() {
	await nextTick();
	if (!fullscreenWidget.value) return;

	for (const instance of widgetRefs.values()) {
		if (instance?.isFullscreen && typeof instance.toggleFullscreen === 'function') {
			instance.toggleFullscreen();
			break;
		}
	}
}

function handleWindowResize() {
	activeGridColumns.value = getGridColumnsForViewport(window.innerWidth);
	const document = iframeDataBridge.getIframeWindow().document;
	const widgetLayout = document.querySelector('.widget-layout');

	if (widgetLayout && widgetLayout.style.height !== 'auto') {
		const hasFullScreenWidget = document.querySelector('.widget.fullscreen-widget');
		resizeLayout({ fullScreened: !!hasFullScreenWidget, widget: getFullscreenWidgetKey(document) });
	}

	isTablet.value = deviceUtils.isTablet();
}

function getFullscreenWidgetKey(document, payload = null) {
	if (payload?.fullScreened === false) {
		return null;
	}

	if (payload?.widget) {
		return payload.widget;
	}

	const fullscreenWidgetElement = document.querySelector('.widget.fullscreen-widget');
	if (!fullscreenWidgetElement) {
		return null;
	}

	return Array.from(fullscreenWidgetElement.classList).find((className) => className.startsWith('widget-') && className !== 'widget-hidden') || null;
}

function resizeLayout(payload) {
	const document = iframeDataBridge.getIframeWindow().document;
	const layout = document.querySelector('.widget-layout');
	const widgetDateContainer = document.querySelector('.widget-date-container');
	const activeFullscreenWidget = getFullscreenWidgetKey(document, payload);
	const isMyDayFullscreen = activeFullscreenWidget === 'widget-myday';

	const pageHeaderContent = document.querySelector('.page-header-content');
	const mainNav = document.querySelector('main-nav nav');
	const contentWrapper = document.querySelector('.content-wrapper');
	const oldWidgets = iframeDocument.querySelectorAll('.wcolumn');

	fullscreenWidget.value = activeFullscreenWidget;

	if (layout) {
		const widgetDateContainerHeight = isMyDayFullscreen ? widgetDateContainer?.offsetHeight || 56 : 0;
		const buffer = deviceUtils.isMobile() ? -30 + widgetDateContainerHeight : 30;
		const height = document.documentElement.clientHeight - (pageHeaderContent && isVisible(pageHeaderContent) ? pageHeaderContent.offsetHeight : 0) - (mainNav && isVisible(mainNav) ? mainNav.offsetHeight : 0) - buffer;
		layout.style.height = payload?.fullScreened ? `${height}px` : 'auto';
		const contentWrapperHeight = contentWrapper ? document.documentElement.clientHeight - (mainNav ? mainNav.offsetHeight : 0) : 0;
		contentWrapper.style.height = payload?.fullScreened ? `${contentWrapperHeight}px` : '100%';
		contentWrapper.style.overflowY = payload?.fullScreened ? 'hidden' : 'auto';

		oldWidgets.forEach((widget) => {
			widget.style.display = payload?.fullScreened ? 'none' : 'block';
		});

		requestAnimationFrame(async () => {
			if (payload?.callback) {
				await payload.callback();
			}

			contentWrapper.scrollTo({ top: 0, behavior: 'smooth' });
		});
	}
}

function isVisible(el) {
	return el && window.getComputedStyle(el).display !== 'none';
}

// #endregion
</script>

<style lang="scss">
.old-widget-config-icon {
	cursor: pointer;
	width: 24px;
	height: 24px;
	font-size: 24px;
	color: var(--v-theme-on-surface);
	position: absolute;
	top: 16px;
	right: 7px;
}
</style>
